import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useLocalizedNavigate from 'hooks/useLocalizedNavigate';
import styles from './gameCarousel.module.scss';
import GameCard, { Game } from 'components/gameCard/gameCard';
import { ChevronLeftIcon, ChevronRightIcon } from 'components/icons';
import GameCarouselSkeleton from './gameCarouselSkeleton';

interface GameCarouselProps {
  title?: React.ReactNode;
  games?: Game[];
  autoplay?: boolean;
  autoplayInterval?: number;
  categoryRoute?: string;
  slug?: string;
  loading?: boolean;
  hideSeeAll?: boolean;
}

const GameCarousel: React.FC<GameCarouselProps> = ({
  title,
  games = [],
  autoplay = false,
  autoplayInterval = 3000,
  categoryRoute,
  slug,
  loading = false,
  hideSeeAll = false,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const navigate = useLocalizedNavigate();
  const { t } = useTranslation();

  const scrollLeft = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth } = scrollRef.current;
      // If at the start (with 15px threshold), smooth scroll to the end
      if (scrollLeft <= 15) {
        scrollRef.current.scrollTo({ left: scrollWidth, behavior: 'smooth' });
      } else {
        scrollRef.current.scrollBy({ left: -300, behavior: 'smooth' });
      }
    }
  };

  const scrollRight = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      // If at the end (with 15px threshold), smooth scroll back to the start
      if (scrollLeft >= scrollWidth - clientWidth - 15) {
        scrollRef.current.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        scrollRef.current.scrollBy({ left: 300, behavior: 'smooth' });
      }
    }
  };

  // Autoplay Logic
  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (autoplay && !loading) {
      intervalId = setInterval(() => {
        scrollRight();
      }, autoplayInterval);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [autoplay, autoplayInterval, loading]);

  if (loading) {
    return <GameCarouselSkeleton />;
  }

  // Auto-derive target route prioritizing slug if passed
  let targetPath = categoryRoute;
  if (!targetPath && slug) {
    targetPath = `/casino/categories/${slug}`;
  } else if (!targetPath && typeof title === 'string') {
    const cleanTitle = title
      .replace(/[^\w\s-]/gi, '')
      .trim()
      .toLowerCase();
    if (cleanTitle) {
      targetPath = `/casino/categories/${cleanTitle.replace(/\s+/g, '-')}`;
    }
  }

  const handleSeeAll = () => {
    if (targetPath) {
      navigate(targetPath);
    }
  };

  // Handle Pause on Hover
  const handleMouseEnter = () => {
    if (autoplay) scrollRef.current?.setAttribute('data-paused', 'true');
  };

  const handleMouseLeave = () => {
    if (autoplay) scrollRef.current?.removeAttribute('data-paused');
  };

  return (
    <div className={styles.carouselContainer}>
      <div className={styles.header}>
        <div className={styles.title}>{title}</div>
        <div className={styles.navControls}>
          {!hideSeeAll && (
            <button
              className={styles.seeAllBtn}
              aria-label={t('common.seeAllGames', 'See all games')}
              onClick={handleSeeAll}
              style={{ opacity: targetPath ? 1 : 0.5, cursor: targetPath ? 'pointer' : 'default' }}
            >
              {t('common.seeAll', 'See All')}
            </button>
          )}
          {games.length > 8 && (
            <>
              <button className={styles.navBtn} onClick={scrollLeft} aria-label="Scroll left">
                <ChevronLeftIcon width="18" height="18" />
              </button>
              <button className={styles.navBtn} onClick={scrollRight} aria-label="Scroll right">
                <ChevronRightIcon width="18" height="18" />
              </button>
            </>
          )}
        </div>
      </div>

      <div
        className={styles.track}
        ref={scrollRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        {games.map((game) => (
          <GameCard key={game.id} game={game} />
        ))}
      </div>
    </div>
  );
};

export default GameCarousel;
